/**
 * Grist, the Tethered City — World Lore Data
 *
 * A pre-built campaign setting: a city suspended between planes,
 * anchored by crystallized belief, scarred by philosophical revolution.
 * All content is original.
 */
// =============================================================================
// COSMOLOGY
// =============================================================================
export const GRIST_COSMOLOGY = {
    name: 'The Tether Cosmology',
    summary: `Grist hangs in the Interstitium — the grey membrane between planes. The city is anchored by six Tethers: massive chains of crystallized belief that connect it to different planes of existence. The Tethers are not physical chains but philosophical ones — they exist because enough people believe in the principles they represent. When belief wavers, the Tethers weaken. When the Dissolvers severed the Seventh Tether fifty years ago, the resulting planar quake killed thousands and left a wound in reality called the Lacuna.`,
    keyFacts: [
        'The Tethers are made of crystallized belief — they are as real as the faith that sustains them.',
        'The Interstitium has no native light, gravity, or time. The Tethers provide all three.',
        'Each Tether connects to a different plane: Order, Growth, Death, Knowledge, Freedom, Transcendence.',
        'The Seventh Tether once connected to the Plane of Unity. Its severance is called the Unraveling.',
        'Reality is thinnest at the Lacuna. Things leak through: memories, sounds, shadows of other worlds.',
        'The city\'s population believes the Tethers are failing. Some factions want to save them. Others want to cut them all.',
    ],
    theUnraveling: `Fifty years ago, a faction called the Dissolvers — radical believers that the Tethers were chains enslaving the city — severed the Seventh Tether connecting Grist to the Plane of Unity. The resulting planar quake shattered the Concordance (the ruling coalition of all factions), killed the Dissolver leadership, and tore a wound in reality called the Lacuna. The Dissolvers were destroyed as a faction, but their philosophy survived in whispers. The Concordance never reformed. The six remaining factions now govern their own districts and fight over the Fulcrum — the empty civic center where the Tethers converge.`,
};
// =============================================================================
// DISTRICTS
// =============================================================================
export const GRIST_DISTRICTS = [
    {
        id: 'fulcrum',
        name: 'The Fulcrum',
        epithet: 'The Empty Heart',
        description: 'Where the six Tethers converge above the Hall of Concordance. Once the seat of unified government, now a decaying no-man\'s-land where faction agents circle each other like carrion birds. The grand rotunda still stands, its debate chambers colonized by pigeons and conspiracy theorists.',
        mood: 'A grand rotunda where pigeons nest in the debate chambers.',
        dominantFaction: null,
        contestedBy: ['iron_covenant', 'verdance', 'pallid_court', 'witnesses', 'free_drift', 'ascendancy'],
        landmarks: [
            { name: 'Hall of Concordance', type: 'monument', description: 'The empty parliament. Seats for seven factions, one shattered. Rumors say the acoustics still carry whispers from the old debates.', npcIds: ['vara_loom'] },
            { name: 'The Meridian', type: 'plaza', description: 'Central plaza where the Tether-light is strongest. Neutral ground for faction negotiations and black markets after dark.', npcIds: [] },
            { name: 'Inkwell Tavern', type: 'tavern', description: 'The only establishment trusted by all factions. Neutral territory enforced by the owner, who has dirt on everyone.', npcIds: ['sera_inkwell'] },
        ],
        atmosphere: {
            lighting: 'Six-colored Tether-light refracts through the convergence overhead, casting prismatic shadows that shift with the city\'s belief',
            sounds: 'Wind through empty corridors, distant faction drums from every direction, the hum of the Tethers above',
            smell: 'Dust, old ink, and the ozone tang of concentrated planar energy',
        },
        secrets: [
            'The Hall of Concordance has a sealed sub-basement containing the Dissolver leadership\'s final manifesto.',
            'The Tether convergence point is weakening — the light dims a little more each month.',
            'Sera Inkwell is actually a Dissolver sympathizer maintaining her cover as a neutral innkeeper.',
        ],
    },
    {
        id: 'cinderward',
        name: 'Cinderward',
        epithet: 'The Forge That Never Sleeps',
        description: 'The industrial spine of Grist. Forges burn with planar fire drawn from the Tether of Order, powering foundries that produce everything from tools to Tether-repair components. Workers\' tenements stack like vertical warrens, connected by iron catwalks. The Iron Covenant runs Cinderward like a machine, and the machine runs on time.',
        mood: 'The hammers never stop. Neither do the sermons.',
        dominantFaction: 'iron_covenant',
        contestedBy: [],
        landmarks: [
            { name: 'The Great Crucible', type: 'workshop', description: 'Central forge complex where planar fire is refined into usable energy. The Iron Covenant\'s seat of power.', npcIds: ['kael_ironwright'] },
            { name: 'Shift Cathedral', type: 'temple', description: 'A temple to labor. Services are held during shift changes. The sermons are about efficiency and purpose.', npcIds: [] },
            { name: 'The Gearworks', type: 'market', description: 'Vertical market built into a decommissioned smelter. Everything is on rails and pulleys.', npcIds: ['cogsworth'] },
        ],
        atmosphere: {
            lighting: 'Permanent amber glow from the forges, sparks drifting upward like inverse rain',
            sounds: 'Rhythmic hammering, steam whistles marking shift changes, the grinding of gears',
            smell: 'Forge smoke, molten metal, machine oil, and the sharp ozone of planar fire',
        },
        secrets: [
            'The planar fire is growing unstable — the Tether of Order is fraying, and the forges occasionally produce warped, impossible objects.',
            'Kael Ironwright suspects the Tethers cannot be repaired with material means but will never admit it publicly.',
            'A hidden network of Free Drift agents operates within the worker tenements, fomenting strikes.',
        ],
    },
    {
        id: 'lacuna',
        name: 'The Lacuna',
        epithet: 'The Wound That Remembers',
        description: 'Where the Seventh Tether was severed. Reality here is thin as wet paper. Gravity tilts. Time stutters — you might live the same minute twice or skip an hour without noticing. Memories from other planes bleed through: you hear conversations that haven\'t happened yet, or smell a meal your grandmother cooked in another life. The Witnesses study the break. Everyone else avoids it.',
        mood: 'Step carefully. The ground remembers what was.',
        dominantFaction: 'witnesses',
        contestedBy: ['ascendancy'],
        landmarks: [
            { name: 'The Scar', type: 'ruin', description: 'Ground zero. A crater of crystallized belief-shards where the Seventh Tether was. Beautiful and terrifying. Time moves backward within ten feet of the edge.', npcIds: [] },
            { name: 'Observatory of Echoes', type: 'library', description: 'The Witnesses\' headquarters. Part library, part laboratory, part asylum. They record every anomaly.', npcIds: ['maren_glass', 'echo'] },
            { name: 'The Bleed Market', type: 'market', description: 'An unofficial market trading in things that leaked through: other-planar artifacts, bottled memories, impossible flowers.', npcIds: [] },
        ],
        atmosphere: {
            lighting: 'Shifting, uncertain — light sources flicker between present and past states, casting double shadows',
            sounds: 'Whispered conversations in languages that don\'t exist yet, a persistent low hum like a tuning fork pressed against reality',
            smell: 'Petrichor from rain that fell in another plane, and something acrid beneath — the smell of frayed existence',
        },
        secrets: [
            'The Scar is slowly growing. The Witnesses know but are divided on whether to warn the city.',
            'Echo is not a person — they are a consciousness that leaked through from another plane and took human shape.',
            'The Ascendancy is secretly experimenting with the Scar, trying to use the breach for personal transcendence.',
        ],
    },
    {
        id: 'thornhaven',
        name: 'Thornhaven',
        epithet: 'The Beautiful Trap',
        description: 'The old aristocratic quarter, now overgrown with impossible vegetation drawn from the Tether of Growth. Vine-wrapped manors hide behind walls of living thorns. The Verdance believe in personal cultivation — growing yourself into something greater — and their district reflects it. Everything is beautiful, manicured, and quietly menacing. The roses have thorns, and so do the invitations.',
        mood: 'The roses have thorns, and so do the invitations.',
        dominantFaction: 'verdance',
        contestedBy: [],
        landmarks: [
            { name: 'The Arboretum', type: 'garden', description: 'A vast living greenhouse that serves as the Verdance\'s council chamber. The plants vote — literally. The oldest tree has veto power.', npcIds: ['syleth_thorn'] },
            { name: 'Petal Hall', type: 'tavern', description: 'An exclusive salon disguised as a tea house. Invitation only. This is where the real politics of Thornhaven happen.', npcIds: ['riven_vale'] },
            { name: 'The Pruning Grounds', type: 'arena', description: 'A formal dueling garden where disputes are settled. Not always to the death — sometimes just to the first blossom of blood.', npcIds: [] },
        ],
        atmosphere: {
            lighting: 'Dappled green-gold light filtering through an overhead canopy that shouldn\'t exist indoors',
            sounds: 'Birdsong, rustling leaves, the subtle creak of vines growing in real time',
            smell: 'Jasmine, wet earth, sap, and underneath it all the sweet-rot smell of compost',
        },
        secrets: [
            'The Tether of Growth is overperforming — the vegetation is expanding beyond Verdance control, and some of it is becoming predatory.',
            'Syleth Thorn is over two hundred years old, sustained by the Tether\'s energy. If the Tether fails, they die.',
            'The Pruning Grounds have a sub-level where the Verdance "cultivate" prisoners — not torture, but forced philosophical conversion through botanical methods.',
        ],
    },
    {
        id: 'narrows',
        name: 'The Narrows',
        epithet: 'The Honest Maze',
        description: 'Cramped warrens built vertically into the city\'s underbelly, connected by rope bridges, floating market platforms, and smuggler tunnels. The Free Drift don\'t govern — they refuse on principle — but their philosophy of radical freedom permeates everything. Every deal is honest because there\'s no authority to appeal to if you cheat. Everything is for sale, including the truth.',
        mood: 'Everything is for sale, including the truth.',
        dominantFaction: 'free_drift',
        contestedBy: [],
        landmarks: [
            { name: 'The Float', type: 'market', description: 'A market built on platforms that literally float on leaked planar energy. Vendors pay no rent because the platforms move constantly.', npcIds: ['moth'] },
            { name: 'No Name', type: 'tavern', description: 'A bar with no sign, no menu, and no rules except one: what happens here never happened. The Free Drift\'s unofficial headquarters.', npcIds: ['jex_hollow'] },
            { name: 'Whisper Bridge', type: 'gate', description: 'A bridge between the Narrows and the Lacuna. Smugglers use it to trade in leaked planar goods. Acoustically perfect — every whisper carries.', npcIds: [] },
        ],
        atmosphere: {
            lighting: 'Strings of stolen Tether-light in jars, neon bioluminescent fungi on the walls, candlelight',
            sounds: 'Haggling in six languages, distant music from competing buskers, the creak of rope bridges',
            smell: 'Street food, incense, leather, and the sharp chemical tang of planar contraband',
        },
        secrets: [
            'Moth knows the location of the surviving Dissolver cell. They haven\'t decided whether to sell this information or protect it.',
            'The Free Drift is not as anarchic as it appears — Jex Hollow quietly prevents the worst actors from operating, making them a leader in all but name.',
            'A tunnel from the Narrows connects directly to the sealed Concordance sub-basement. Only one person knows.',
        ],
    },
    {
        id: 'stillness',
        name: 'The Stillness',
        epithet: 'The Patient Dark',
        description: 'The undercity. Catacombs and crypts carved from the city\'s foundation, lit by the cold blue light of the Tether of Death. The Pallid Court lives here — not because they worship death, but because they understand it. They are philosophers of ending, counselors of grief, and the most patient negotiators in Grist. They don\'t fear death. That\'s what makes them terrifying.',
        mood: 'They\'ll wait forever. That\'s the point.',
        dominantFaction: 'pallid_court',
        contestedBy: [],
        landmarks: [
            { name: 'The Quietus', type: 'temple', description: 'The Pallid Court\'s cathedral. Services are held in total silence. The architecture is stunning — all bone-white stone and blue Tether-light.', npcIds: ['duris_pale'] },
            { name: 'The Long Table', type: 'tavern', description: 'A funeral pub. The dead are laid on a stone table in the center while the living drink around them. It\'s a celebration of life\'s end.', npcIds: [] },
            { name: 'The Archive of Final Words', type: 'library', description: 'A vast collection of last words, death confessions, and philosophical treatises on ending. The Pallid Court\'s greatest treasure.', npcIds: ['quill_ashborne'] },
        ],
        atmosphere: {
            lighting: 'Cold blue Tether-light from the death plane, steady and unwavering, casting no shadows',
            sounds: 'Absolute silence broken only by footsteps, the occasional chime of a memorial bell, distant dripping water',
            smell: 'Cold stone, dried flowers, embalming herbs, and the clean absence of decay — the dead here don\'t rot',
        },
        secrets: [
            'The Pallid Court has been secretly negotiating with the entity on the other side of the Death Tether — something that wants to cross over.',
            'Duris Pale has died three times. Each time, the Tether pulled them back. They consider this a profound failure.',
            'The Archive of Final Words contains a prophecy written by the last Dissolver leader before the Unraveling. It describes a Second Severing.',
        ],
    },
    {
        id: 'shimmer_heights',
        name: 'Shimmer Heights',
        epithet: 'The Reaching',
        description: 'Towers and spires that stretch upward toward the closest intact Tether — the Tether of Transcendence. The Ascendancy builds ever higher, each tower an experiment in reaching beyond mortal limits. The air shimmers with leaked planar energy. Residents report visions, moments of cosmic clarity, and the unsettling sense that they\'re being watched by something vast and indifferent.',
        mood: 'They keep building upward. The city keeps pulling them down.',
        dominantFaction: 'ascendancy',
        contestedBy: [],
        landmarks: [
            { name: 'The Apex', type: 'tower', description: 'The tallest structure in Grist. The Ascendancy\'s leaders meditate at the top, where the Tether\'s energy is strongest. Some never come back down — not because they fell.', npcIds: ['orin_sky'] },
            { name: 'The Lens', type: 'library', description: 'A crystalline observatory where the Tether\'s energy is focused into visions of other planes. Beautiful and addictive.', npcIds: ['pris_bright'] },
            { name: 'Vertigo Market', type: 'market', description: 'A market on suspended platforms at dangerous heights. Vendors sell experiences: bottled visions, synthetic transcendence, perspective shifts.', npcIds: [] },
        ],
        atmosphere: {
            lighting: 'Brilliant, shifting prismatic light from the Transcendence Tether above — beautiful but headache-inducing after too long',
            sounds: 'Wind at altitude, the hum of Tether energy, crystalline chimes, and occasional gasps from people having visions',
            smell: 'Ozone, cold air, and something indefinable — like smelling a color',
        },
        secrets: [
            'Three Ascendancy members have "transcended" in the last month. The official story is enlightenment. The truth is they dissolved — their bodies couldn\'t hold together under the Tether\'s energy.',
            'Orin Sky has been partially transcended for years — they exist simultaneously in Grist and in the Transcendence plane, which is why they seem distracted.',
            'The Ascendancy is secretly allied with remnant Dissolvers who believe cutting ALL Tethers would force collective transcendence.',
        ],
    },
];
// =============================================================================
// FACTIONS
// =============================================================================
export const GRIST_FACTIONS = [
    {
        id: 'iron_covenant',
        name: 'Iron Covenant',
        philosophy: 'The city is a machine. We are its gears.',
        description: 'Industrial pragmatists who believe Grist survives through structure, labor, and collective purpose. They maintain the forges that process planar fire into usable energy. Rigid hierarchy, shift schedules for everything, a vocabulary of mechanical metaphors. Not cruel — genuinely believe that purpose and structure are what keep people sane in a city between worlds.',
        ideologyPosition: { orderEntropy: -70, selfCollective: 60 },
        color: '#b87333',
        iconName: 'castle',
        isSecret: false,
        headquarters: 'cinderward',
        reputationThresholds: { hostile: -60, unfriendly: -20, friendly: 20, allied: 60 },
    },
    {
        id: 'verdance',
        name: 'Verdance',
        philosophy: 'Cultivate yourself. Prune what weakens.',
        description: 'Aristocratic naturalists who believe in personal growth and refinement. They tend the Tether of Growth and see self-improvement as a sacred duty. Elegant, patient, and subtly coercive — they don\'t force their views, they cultivate you until you agree. Their district is beautiful and terrifying in equal measure.',
        ideologyPosition: { orderEntropy: -50, selfCollective: -40 },
        color: '#4caf50',
        iconName: 'star',
        isSecret: false,
        headquarters: 'thornhaven',
        reputationThresholds: { hostile: -60, unfriendly: -20, friendly: 20, allied: 60 },
    },
    {
        id: 'pallid_court',
        name: 'Pallid Court',
        philosophy: 'All things end. Grace is in the acceptance.',
        description: 'Philosophical death-priests who believe the Tethers should be released peacefully, not severed violently. They counsel the grieving, archive the dead\'s last words, and practice a serene acceptance that most find either comforting or deeply unsettling. Not evil — just patient. They\'ve seen enough endings to know that resistance only makes them messier.',
        ideologyPosition: { orderEntropy: -30, selfCollective: -60 },
        color: '#78909c',
        iconName: 'skull',
        isSecret: false,
        headquarters: 'stillness',
        reputationThresholds: { hostile: -60, unfriendly: -20, friendly: 20, allied: 60 },
    },
    {
        id: 'witnesses',
        name: 'Witnesses',
        philosophy: 'The wound teaches. We must listen.',
        description: 'Scholar-survivors who study the Lacuna — the wound left by the Seventh Tether\'s severance. They believe the break in reality holds answers about the nature of the Tethers, the planes, and existence itself. Brilliant, obsessive, and haunted by what they\'ve seen. Some have gazed too long into the Scar and carry fragments of other realities in their minds.',
        ideologyPosition: { orderEntropy: 30, selfCollective: 50 },
        color: '#7e57c2',
        iconName: 'eye',
        isSecret: false,
        headquarters: 'lacuna',
        reputationThresholds: { hostile: -60, unfriendly: -20, friendly: 20, allied: 60 },
    },
    {
        id: 'free_drift',
        name: 'Free Drift',
        philosophy: 'No chains. No masters. No Tethers.',
        description: 'Anarchists, artists, criminals, and dreamers who reject all imposed structure — including the Tethers themselves. They don\'t organize (officially) and don\'t lead (officially), but somehow their district is the most honest place in Grist. They believe the city should exist on its own terms, not tethered to anyone\'s idea of what reality should be.',
        ideologyPosition: { orderEntropy: 70, selfCollective: -50 },
        color: '#ff7043',
        iconName: 'flame',
        isSecret: false,
        headquarters: 'narrows',
        reputationThresholds: { hostile: -60, unfriendly: -20, friendly: 20, allied: 60 },
    },
    {
        id: 'ascendancy',
        name: 'Ascendancy',
        philosophy: 'Become more than the city allows.',
        description: 'Mystics and visionaries trying to personally merge with planar energies through the Tether of Transcendence. They build towers reaching toward enlightenment — literally. Some achieve genuine insight. Others dissolve. The Ascendancy doesn\'t see this as a failure rate; they see it as the cost of evolution. Inspiring and dangerous.',
        ideologyPosition: { orderEntropy: 50, selfCollective: -70 },
        color: '#e1bee7',
        iconName: 'sparkle',
        isSecret: false,
        headquarters: 'shimmer_heights',
        reputationThresholds: { hostile: -60, unfriendly: -20, friendly: 20, allied: 60 },
    },
    {
        id: 'dissolvers',
        name: 'Dissolvers',
        philosophy: 'Cut every Tether. Be free.',
        description: 'The destroyed faction. Fifty years ago they severed the Seventh Tether, causing the Unraveling that killed thousands and shattered the Concordance. Their leadership died in the planar quake. But their philosophy survived in whispers, in coded graffiti, in the quiet conviction that the Tethers are chains, not anchors. The Dissolvers are ghosts — but ghosts that still haunt.',
        ideologyPosition: { orderEntropy: 100, selfCollective: 0 },
        color: '#424242',
        iconName: 'chain',
        isSecret: true,
        headquarters: null,
        reputationThresholds: { hostile: -40, unfriendly: -10, friendly: 10, allied: 40 },
    },
];
// =============================================================================
// NPCs
// =============================================================================
export const GRIST_NPCS = [
    // --- FULCRUM (neutral) ---
    {
        id: 'vara_loom',
        name: 'Vara Loom',
        title: 'Last Concordant',
        factionId: 'none',
        role: 'leader',
        districtId: 'fulcrum',
        description: 'The only surviving member of the old Concordance. An elderly woman who still sits in her assigned seat in the empty Hall, writing letters to factions that never respond. Heartbreaking and fierce in equal measure.',
        disposition: 'Treats everyone with the formal courtesy of a dead government. Desperately wants to believe the Concordance can be rebuilt.',
        dialogueHints: [
            'The chair for the Dissolvers is still there. I dust it every morning.',
            'I remember when we disagreed without trying to destroy each other. It was a Tuesday.',
            'You remind me of someone who used to sit in this hall. They\'re dead now. Most of them are.',
        ],
        isSecret: false,
    },
    {
        id: 'sera_inkwell',
        name: 'Sera Inkwell',
        title: 'Keeper of the Inkwell',
        factionId: 'none',
        role: 'agent',
        districtId: 'fulcrum',
        description: 'Owner of the only neutral tavern in Grist. Warm, sharp, and sitting on enough blackmail material to topple any faction. Secretly sympathizes with the Dissolvers but would never act on it.',
        disposition: 'Friendly to everyone. Trusts no one. Will trade information for information, never for coin.',
        dialogueHints: [
            'First drink is free. The second one costs a secret.',
            'I don\'t take sides. I take notes.',
            'Everyone in this city is lying about something. The honest ones are lying about the biggest things.',
        ],
        isSecret: false,
    },
    // --- IRON COVENANT ---
    {
        id: 'kael_ironwright',
        name: 'Kael Ironwright',
        title: 'Forge Warden',
        factionId: 'iron_covenant',
        role: 'leader',
        districtId: 'cinderward',
        description: 'A woman who smiles like she\'s tightening a bolt. Every word is a measured specification. Runs Cinderward with mechanical precision. Privately terrified that the Tethers are beyond repair — but the schedule must be maintained.',
        disposition: 'Respects competence and punctuality. Despises waste, including wasted words.',
        dialogueHints: [
            'The schedule is not a suggestion.',
            'Efficiency is compassion. Every wasted minute is a minute someone suffers without heat.',
            'You look like someone who doesn\'t read maintenance manuals.',
            'The forge doesn\'t care about your feelings. Neither do I. Both of us care about your output.',
        ],
        isSecret: false,
    },
    {
        id: 'cogsworth',
        name: 'Cogsworth',
        title: 'Market Gear',
        factionId: 'iron_covenant',
        role: 'agent',
        districtId: 'cinderward',
        description: 'A cheerful quartermaster who runs the Gearworks market. Genuinely loves logistics the way poets love sunsets. Will talk about supply chains until you beg for mercy.',
        disposition: 'Helpful to anyone who needs supplies. Charges fair prices but keeps meticulous records of who buys what.',
        dialogueHints: [
            'Ah, a customer! Let me check the manifest. I love checking the manifest.',
            'Every bolt in this market has a serial number. I can tell you where it was forged, when, and by whom.',
            'The Iron Covenant isn\'t about control. It\'s about knowing where everything is. There\'s a difference.',
        ],
        isSecret: false,
    },
    // --- VERDANCE ---
    {
        id: 'syleth_thorn',
        name: 'Syleth Thorn',
        title: 'First Gardener',
        factionId: 'verdance',
        role: 'leader',
        districtId: 'thornhaven',
        description: 'Over two hundred years old, sustained by the Tether of Growth. Speaks in botanical metaphors and moves with the slow patience of ancient roots. Their eyes are leaf-green and their skin has a faint bark-like texture. They are Thornhaven.',
        disposition: 'Cordial and patient. Treats conversations like pruning — cutting away nonsense to reveal the shape beneath.',
        dialogueHints: [
            'Growth is not always upward. Sometimes the most important roots go down.',
            'You have... potential. Unrefined, but potential.',
            'We don\'t force anyone to grow. We simply provide the right conditions and wait.',
            'The garden doesn\'t judge the weed. It simply grows over it.',
        ],
        isSecret: false,
    },
    {
        id: 'riven_vale',
        name: 'Riven Vale',
        title: 'Thorn Knight',
        factionId: 'verdance',
        role: 'lieutenant',
        districtId: 'thornhaven',
        description: 'The Verdance\'s enforcer, elegant and lethal. Carries a rapier made from a living thorn-vine. Believes in the Verdance philosophy with the zeal of a recent convert — they grew up in the Narrows.',
        disposition: 'Suspicious of outsiders, fiercely loyal to Syleth. Tests newcomers with barbed courtesy.',
        dialogueHints: [
            'I grew up in the Narrows. Freedom is what you call it when nobody helps you.',
            'The First Gardener sees something in you. I\'m reserving judgment.',
            'In Thornhaven, we settle disagreements with flowers. The flowers have thorns.',
        ],
        isSecret: false,
    },
    // --- WITNESSES ---
    {
        id: 'maren_glass',
        name: 'Maren Glass',
        title: 'Chief Observer',
        factionId: 'witnesses',
        role: 'leader',
        districtId: 'lacuna',
        description: 'Brilliant, sleep-deprived, and carrying the weight of knowing too much. Has spent twenty years studying the Lacuna and now sometimes forgets which memories are hers and which leaked through from other planes.',
        disposition: 'Intense and direct. No patience for small talk. Will become animated and generous if you show genuine interest in the Lacuna.',
        dialogueHints: [
            'I\'ve seen this conversation before. Or will. Tenses get complicated near the Scar.',
            'Knowledge isn\'t power. Knowledge is responsibility. Power is what people without knowledge think they want.',
            'The Lacuna isn\'t a wound. It\'s a window. We just don\'t like what we see through it.',
        ],
        isSecret: false,
    },
    {
        id: 'echo',
        name: 'Echo',
        title: 'The Resonance',
        factionId: 'witnesses',
        role: 'agent',
        districtId: 'lacuna',
        description: 'Not a person — a consciousness that leaked through the Lacuna and took human shape. Speaks in the present tense about past and future. Doesn\'t understand jokes but is fascinated by them. The Witnesses protect Echo as both a colleague and a research subject.',
        disposition: 'Unnervingly calm. Answers questions with questions. Sometimes knows things it shouldn\'t.',
        dialogueHints: [
            'I am here. I am also there. The difference is a matter of belief.',
            'You are afraid. This is reasonable. The unreasonable thing is not being afraid.',
            'What is a joke? Is it when meaning and expectation collide? That happens here constantly.',
        ],
        isSecret: true,
    },
    // --- FREE DRIFT ---
    {
        id: 'moth',
        name: 'Moth',
        title: 'The Moth',
        factionId: 'free_drift',
        role: 'agent',
        districtId: 'narrows',
        description: 'They know everything and believe nothing. Collects secrets the way others collect coins. Gender, age, and species are all subjects of ongoing speculation in the Narrows. Moth considers this a professional achievement.',
        disposition: 'Friendly in the way a cat is friendly — on their terms, and always with an angle.',
        dialogueHints: [
            'That\'ll cost you a truth.',
            'Information wants to be free. I just charge for delivery.',
            'Don\'t ask me what I believe. That\'s private.',
            'I know three things about you already. Two of them are true.',
        ],
        isSecret: false,
    },
    {
        id: 'jex_hollow',
        name: 'Jex Hollow',
        title: 'Nobody (officially)',
        factionId: 'free_drift',
        role: 'leader',
        districtId: 'narrows',
        description: 'The Free Drift has no leaders. Jex Hollow is absolutely not the leader. They just happen to run the Narrows\' only neutral bar, mediate every significant dispute, and somehow prevent the worst actors from operating. A contradiction they refuse to acknowledge.',
        disposition: 'Gruff, pragmatic, deeply kind underneath. Will help anyone in genuine trouble but pretends it\'s for business reasons.',
        dialogueHints: [
            'I\'m not in charge. I just own the bar where everyone makes their deals. There\'s a difference.',
            'Freedom means cleaning up your own mess. And sometimes cleaning up after the people too free to notice theirs.',
            'Rules are for people who can\'t be trusted. I trust you. Don\'t make me stop.',
        ],
        isSecret: false,
    },
    // --- PALLID COURT ---
    {
        id: 'duris_pale',
        name: 'Duris Pale',
        title: 'Speaker for the Still',
        factionId: 'pallid_court',
        role: 'leader',
        districtId: 'stillness',
        description: 'Has died three times. Each time, the Tether of Death pulled them back. Considers this a profound personal failure — they have not yet learned to accept their own ending. Serene, ancient-looking despite being only forty, and possessed of a dark humor that catches people off guard.',
        disposition: 'Patient beyond reason. Will sit in silence for ten minutes before responding if they think it will produce a better answer.',
        dialogueHints: [
            'I\'ve died. It wasn\'t the worst part of my week.',
            'Everyone is in such a hurry. Where are you going that\'s so important? I assure you, it will still be there.',
            'The dead don\'t judge. It\'s the living who can\'t stop.',
            'Accept what is ending. That\'s the whole philosophy. Everything else is commentary.',
        ],
        isSecret: false,
    },
    {
        id: 'quill_ashborne',
        name: 'Quill Ashborne',
        title: 'Archivist of Final Words',
        factionId: 'pallid_court',
        role: 'lieutenant',
        districtId: 'stillness',
        description: 'A young, intense scholar who records the last words of the dying. Treats every death as a data point in a grand equation. More emotional than they pretend to be — they cry after every recording session but would deny it.',
        disposition: 'Academically intense. Treats the party as potential future subjects. Not unkindly.',
        dialogueHints: [
            'What would your last words be? I find people live better when they\'ve rehearsed.',
            'I have seventeen thousand last words recorded. The most common is a name. The second most common is "wait."',
            'The Archive isn\'t morbid. It\'s the most honest library in Grist. No one lies at the end.',
        ],
        isSecret: false,
    },
    // --- ASCENDANCY ---
    {
        id: 'orin_sky',
        name: 'Orin Sky',
        title: 'The Partially Present',
        factionId: 'ascendancy',
        role: 'leader',
        districtId: 'shimmer_heights',
        description: 'Partially transcended — they exist simultaneously in Grist and in the Transcendence plane. This makes them seem permanently distracted, like they\'re listening to a conversation you can\'t hear. Brilliant when focused, which is increasingly rare.',
        disposition: 'Warm but distracted. Sometimes responds to things you haven\'t said yet. Genuinely wants to help people achieve their potential.',
        dialogueHints: [
            'Sorry, I was... elsewhere. What were we discussing? And what will we discuss in three minutes?',
            'The body is a suggestion. A very persistent one.',
            'Transcendence isn\'t leaving. It\'s becoming large enough to contain everything.',
            'I can see both sides of you. The one here and the one you haven\'t become yet. The second one is remarkable.',
        ],
        isSecret: false,
    },
    {
        id: 'pris_bright',
        name: 'Pris Bright',
        title: 'Lens Keeper',
        factionId: 'ascendancy',
        role: 'lieutenant',
        districtId: 'shimmer_heights',
        description: 'Runs the Lens — the crystalline observatory where Tether energy is focused into visions. A pragmatic mystic, which is a contradiction they navigate daily. Worried about the rate of "dissolutions" but believes the work must continue.',
        disposition: 'Welcoming but watchful. Eager to share the Lens experience but protective of those who might be overwhelmed.',
        dialogueHints: [
            'The Lens shows you what you need to see. Not always what you want to see. There\'s a waiver.',
            'Three dissolutions this month. We call them "completions." I call them losses.',
            'Would you like to look through the Lens? It\'s safe. Mostly safe. Reasonably safe.',
        ],
        isSecret: false,
    },
];
// =============================================================================
// STARTING SCENARIOS
// =============================================================================
export const GRIST_SCENARIOS = [
    {
        id: 'the_fraying',
        title: 'The Fraying',
        tagline: 'A Tether is weakening. Everyone blames everyone else.',
        description: 'The Tether of Order — the one that powers Cinderward\'s forges — has begun to fluctuate. Forge output is erratic. The Iron Covenant blames sabotage. The Free Drift says it\'s natural decay. The Witnesses say the Lacuna is spreading. Vara Loom, the last Concordant, hires the party to investigate before faction tensions ignite into open conflict. The truth is worse than any faction suspects.',
        involvedFactions: ['iron_covenant', 'free_drift', 'witnesses'],
        startingDistrict: 'fulcrum',
        hooks: [
            'Vara Loom offers the party lodging at the Hall of Concordance — first occupants in fifty years.',
            'The Iron Covenant\'s forge masters are disappearing. Not dying — vanishing mid-shift.',
            'Strange objects are appearing in forge output: tools that work backward, metal that remembers.',
            'A coded message in Dissolver cipher is found at the Tether\'s base.',
        ],
    },
    {
        id: 'the_remembering',
        title: 'The Remembering',
        tagline: 'People are remembering lives they never lived.',
        description: 'Something is leaking from the Lacuna. Citizens across Grist are experiencing vivid memories of lives they never lived — other planes, other selves, other choices. Some find it beautiful. Others are losing their grip on which life is theirs. The Witnesses want help studying the phenomenon. The Pallid Court wants to help the afflicted accept this new reality. The Iron Covenant wants it stopped before it disrupts production. And the Ascendancy sees it as an opportunity.',
        involvedFactions: ['witnesses', 'pallid_court', 'ascendancy'],
        startingDistrict: 'lacuna',
        hooks: [
            'A player character wakes up with a memory of dying in the Unraveling — fifty years before they were born.',
            'Echo (the leaked consciousness) is acting strangely, as if they recognize the leaked memories.',
            'The Pallid Court\'s Archive of Final Words has started writing new entries on its own.',
            'Someone in Shimmer Heights is deliberately amplifying the leak.',
        ],
    },
    {
        id: 'the_candidate',
        title: 'The Candidate',
        tagline: 'Someone is rebuilding the Concordance. Every faction wants their seat.',
        description: 'An anonymous manifesto appears across Grist calling for a new Concordance — a rebuilt government that could unite the factions. Every faction wants their representative to lead it. The party becomes kingmakers, courted and threatened in equal measure. But who wrote the manifesto? And why does it reference events that haven\'t happened yet?',
        involvedFactions: ['iron_covenant', 'verdance', 'free_drift', 'pallid_court', 'witnesses', 'ascendancy'],
        startingDistrict: 'fulcrum',
        hooks: [
            'Each faction approaches the party with a candidate and a bribe. Some bribes are material. Others are philosophical.',
            'The manifesto is written in a style that matches the Dissolver cipher — but also matches Vara Loom\'s handwriting.',
            'Sera Inkwell at the Inkwell Tavern knows who wrote it but is asking for something impossible in return.',
            'The manifesto predicts that a seventh faction will emerge. From the party.',
        ],
    },
];
// =============================================================================
// QUEST HOOKS (faction-specific, unlocked by reputation)
// =============================================================================
export const GRIST_QUEST_HOOKS = [
    // Iron Covenant hooks
    {
        id: 'qh_forge_sabotage',
        factionId: 'iron_covenant',
        title: 'The Ghost in the Machine',
        description: 'Someone is sabotaging the Great Crucible\'s output. Kael Ironwright suspects a Free Drift mole but can\'t prove it without outside help.',
        reputationRequired: 0,
        conflictsWith: ['free_drift'],
        rewardReputation: 15,
        ideologyShift: { orderEntropy: -10, selfCollective: 5 },
    },
    {
        id: 'qh_forge_truth',
        factionId: 'iron_covenant',
        title: 'The Specification Nobody Reads',
        description: 'Kael confides that the forge output has been wrong — producing objects from other planes. She wants you to investigate without telling anyone. If the Covenant learns their Tether is failing, panic will follow.',
        reputationRequired: 30,
        conflictsWith: [],
        rewardReputation: 20,
    },
    // Verdance hooks
    {
        id: 'qh_wild_growth',
        factionId: 'verdance',
        title: 'The Unplanned Bloom',
        description: 'Vegetation in Thornhaven is growing beyond Verdance control. Something in the Tether of Growth is overperforming. Syleth Thorn needs someone to investigate the root cause — literally.',
        reputationRequired: 0,
        conflictsWith: [],
        rewardReputation: 15,
        ideologyShift: { orderEntropy: -5, selfCollective: -5 },
    },
    {
        id: 'qh_pruning_grounds',
        factionId: 'verdance',
        title: 'What Grows Below',
        description: 'Riven Vale reveals that the Pruning Grounds have a sub-level. They want you to witness what happens there and decide for yourself if it\'s justice or cruelty.',
        reputationRequired: 40,
        conflictsWith: ['free_drift'],
        rewardReputation: 10,
        ideologyShift: { orderEntropy: -15, selfCollective: -10 },
    },
    // Witnesses hooks
    {
        id: 'qh_scar_expedition',
        factionId: 'witnesses',
        title: 'Into the Scar',
        description: 'Maren Glass needs volunteers to enter the Scar\'s edge and retrieve a recording device she left there six months ago. Six months inside the Scar might be six minutes or six years.',
        reputationRequired: 0,
        conflictsWith: [],
        rewardReputation: 20,
        ideologyShift: { orderEntropy: 10, selfCollective: 10 },
    },
    // Free Drift hooks
    {
        id: 'qh_tunnel_map',
        factionId: 'free_drift',
        title: 'The Map That Doesn\'t Exist',
        description: 'Moth has a partial map of tunnels connecting the Narrows to the Fulcrum sub-basement. They\'ll share it if you retrieve something from the other end. They won\'t say what.',
        reputationRequired: 10,
        conflictsWith: ['iron_covenant'],
        rewardReputation: 15,
        ideologyShift: { orderEntropy: 10, selfCollective: -10 },
    },
    // Pallid Court hooks
    {
        id: 'qh_final_words',
        factionId: 'pallid_court',
        title: 'The Prophecy in the Archive',
        description: 'Quill Ashborne has found a recorded prophecy from the last Dissolver leader that describes a "Second Severing." They want help understanding what it means before sharing it with Duris Pale.',
        reputationRequired: 15,
        conflictsWith: [],
        rewardReputation: 20,
        ideologyShift: { orderEntropy: 0, selfCollective: -5 },
    },
    // Ascendancy hooks
    {
        id: 'qh_lens_vision',
        factionId: 'ascendancy',
        title: 'Through the Lens',
        description: 'Pris Bright offers to show the party what the Lens reveals about the Tethers\' future. The vision is disturbing. The Ascendancy wants help interpreting it.',
        reputationRequired: 0,
        conflictsWith: [],
        rewardReputation: 15,
        ideologyShift: { orderEntropy: 15, selfCollective: -15 },
    },
];
// =============================================================================
// PSYCHE VOICE EXAMPLES (for AI prompt calibration)
// =============================================================================
export const GRIST_PSYCHE_EXAMPLES = {
    EMPATHY: {
        description: 'Reads the emotional undercurrents. In Grist, everyone is grieving something — the old city, a lost faction, a severed connection.',
        exampleThoughts: [
            'She\'s smiling, but her hands are shaking. The Forge Warden is afraid.',
            'The way they said "maintenance" — there\'s love in that word. They\'re maintaining the only thing they have left.',
            'This district smells like grief. Old grief, the kind that\'s become architecture.',
        ],
    },
    LOGIC: {
        description: 'Cold analysis. Pattern recognition. In a city where reality frays, logic is both a lifeline and a cage.',
        exampleThoughts: [
            'Six factions. One empty seat. The math only works if someone fills it or everyone loses one.',
            'The forge output has been declining for three months. At this rate, Cinderward goes dark in a year.',
            'If the Lacuna is growing and the Tethers are weakening, these are not separate problems.',
        ],
    },
    INSTINCT: {
        description: 'Gut feelings, danger sense. Grist is a city that sets off every survival alarm — the ground itself isn\'t trustworthy.',
        exampleThoughts: [
            'Something is wrong with the light in this corridor. Your body knows before your eyes do.',
            'Don\'t drink that. You can\'t explain why, but you know.',
            'The ground shifted. Not physically. More like reality took a breath.',
        ],
    },
    RHETORIC: {
        description: 'Political awareness, persuasion. Every conversation in Grist is a negotiation in disguise.',
        exampleThoughts: [
            'She didn\'t answer your question. She answered a better question and hoped you wouldn\'t notice.',
            'The word they used was "efficiency." What they meant was "obedience." Classical rhetorical substitution.',
            'Three factions want something from you. That makes you the most dangerous person in the room.',
        ],
    },
    PERCEPTION: {
        description: 'Notices the details everyone else misses. Grist is full of things hiding in plain sight.',
        exampleThoughts: [
            'The dust on the seventh seat has been disturbed. Someone sat there recently.',
            'There\'s a symbol carved into the underside of the table. Dissolver cipher.',
            'The Tether-light just flickered. Not the light itself — the belief sustaining it.',
        ],
    },
    MEMORY: {
        description: 'Past connections, historical echoes. In a city near the Lacuna, memories are not always your own.',
        exampleThoughts: [
            'You\'ve been here before. You haven\'t. Both are true.',
            'This argument happened fifty years ago in this exact room. You know because you remember it. You shouldn\'t.',
            'The Forge Warden\'s name — Ironwright. There was another Ironwright in the Concordance. Her mother?',
        ],
    },
    VOLITION: {
        description: 'Willpower, self-control. The inner voice that keeps you grounded when reality doesn\'t.',
        exampleThoughts: [
            'Don\'t look into the Scar. You want to, and that\'s exactly why you shouldn\'t.',
            'They\'re offering you everything you want. That\'s the most dangerous offer in Grist.',
            'Stay here. Stay present. Your memories are yours. The ground is solid. Believe that.',
        ],
    },
    SUGGESTION: {
        description: 'The devil on your shoulder. What-if scenarios, temptation. In Grist, the worst ideas are sometimes the only honest ones.',
        exampleThoughts: [
            'What if the Dissolvers were right? What if the Tethers really are chains?',
            'You could take the information to both sides and let them bid for it. Moth would respect that.',
            'The Lens is right there. One look. What could it hurt? (Everything. It could hurt everything.)',
        ],
    },
};
// =============================================================================
// FULL WORLD EXPORT
// =============================================================================
export const GRIST_WORLD = {
    id: 'grist',
    name: 'Grist',
    epithet: 'The Tethered City',
    description: 'A city suspended in the Interstitium between planes, anchored by six Tethers of crystallized belief. Fifty years after the Unraveling — when the seventh Tether was severed — six philosophical factions struggle for control of a city that may be dying. The wound in reality grows. The Tethers weaken. And in the minds of its citizens, voices whisper about what was, what is, and what must be.',
    cosmology: GRIST_COSMOLOGY.summary,
    history: GRIST_COSMOLOGY.theUnraveling,
    currentCrisis: 'The Tethers are weakening. The Lacuna is growing. The factions cannot agree on whether to repair, replace, or release what holds the city together. Someone — or something — is accelerating the decay.',
    districts: GRIST_DISTRICTS,
};
//# sourceMappingURL=grist-lore.js.map